import {
  builtinComponentMatches,
  consolidateBuiltinComponents,
} from "./builtin-component-sharing.mjs";
import {
  normalizeRenderers,
  rendererFields,
  gridSlots,
  cssError,
  getRendererReferences,
} from "./component-renderers.mjs";
import { templateSchema as schema, text, pageUrl } from "./template-pages.mjs";
export const componentTypes = {
  banner: "广告横幅",
  text: "文字内容",
  image: "图片展示",
  "page-link": "页面入口",
  "video-grid": "视频列表",
  "content-list": "内容区块",
  "ad-slot": "广告位区块",
  builtin: "系统区块",
  navigation: "导航菜单",
};
export const defaultComponentCategories = () => [
  { id: "CAT-PAGE", name: "页面组件", sort: 1 },
  { id: "CAT-AD", name: "广告组件", sort: 2 },
  { id: "CAT-SYSTEM", name: "系统组件", sort: 3 },
  { id: "CAT-CONTENT", name: "内容组件", sort: 4 },
];
export function normalizeComponentCategories(value) {
  return (Array.isArray(value) ? value : defaultComponentCategories())
    .slice(0, 100)
    .map((v, i) => ({
      id: text(v?.id, `CAT-${i + 1}`),
      name: text(v?.name, "新分类"),
      sort: Math.max(1, Number(v?.sort) || i + 1),
    }))
    .sort((a, b) => a.sort - b.sort);
}
const bounded = (v, fallback, min, max) =>
  Number.isFinite(Number(v))
    ? Math.min(max, Math.max(min, Math.floor(Number(v))))
    : fallback;
const choice = (v, allowed, fallback) => (allowed.includes(v) ? v : fallback);
export function getCategoryReferences(config, id) {
  return config.components
    .filter((c) => c.categoryId === id)
    .map((c) => `组件“${c.name}”`);
}
export const builtinDependencies = {
  "home:header": ["search"],
  "discover:header": ["search"],
  "discover:videos": ["home"],
  "profile:cover": ["collection", "settings"],
  "profile:info": ["collection"],
  "profile:videos": ["home"],
  "messages:header": ["settings"],
  "search:results": ["home"],
  "collection:content": ["home"],
  "create:camera": ["publish"],
  "publish:footer": ["home", "profile"],
};
export function defaultComponents() {
  return [
    ...schema.pages.flatMap((p) =>
      p.blocks.map((b) => ({
        id: `CMP-${p.id}-${b.id}`,
        name: `${p.name} / ${b.name}`,
        type: "builtin",
        renderer: `${p.id}:${b.id}`,
        status: "启用",
        params: {},
      })),
    ),
    {
      id: "CMP-BANNER",
      name: "广告横幅",
      type: "banner",
      renderer: "",
      status: "启用",
      params: {
        title: "发现你的下一部好剧",
        imageUrl: "/media/lake.jpg",
        url: "#discover",
        radius: 12,
      },
    },
    {
      id: "CMP-TEXT",
      name: "文字内容",
      type: "text",
      renderer: "",
      status: "启用",
      params: {
        title: "精彩内容",
        text: "在组件管理中编辑展示内容。",
        padding: 16,
      },
    },
    {
      id: "CMP-VIDEO-GRID",
      name: "视频列表",
      type: "video-grid",
      renderer: "",
      status: "启用",
      params: { title: "推荐视频", columns: 2, gap: 12 },
    },
    {
      id: "CMP-PAGE-LINK",
      name: "页面入口",
      type: "page-link",
      renderer: "",
      status: "启用",
      params: { title: "返回首页", url: "#home", padding: 16 },
    },
  ];
}
export function normalizeComponents(value) {
  return (Array.isArray(value) ? value : defaultComponents())
    .slice(0, 300)
    .map((raw, i) => {
      const p = raw?.params || {};
      return {
        id: text(raw?.id, `CMP-${i + 1}`),
        name: text(raw?.name, "新组件"),
        type: Object.hasOwn(componentTypes, raw?.type) ? raw.type : "text",
        categoryId: text(
          raw?.categoryId,
          raw?.type === "builtin"
            ? "CAT-SYSTEM"
            : ["banner", "ad-slot"].includes(raw?.type)
              ? "CAT-AD"
              : ["video-grid", "content-list", "text", "image"].includes(
                    raw?.type,
                  )
                ? "CAT-CONTENT"
                : "CAT-PAGE",
        ),
        renderer: text(raw?.renderer),
        rendererId: text(
          raw?.rendererId,
          `R-${Object.hasOwn(componentTypes, raw?.type) ? raw.type : "text"}`,
        ),
        status: raw?.status === "停用" ? "停用" : "启用",
        params: {
          title: text(p.title),
          text: text(p.text, "", 4000),
          imageUrl: text(p.imageUrl, "", 2000),
          url: text(p.url, "", 2000),
          gap: Math.min(48, Math.max(0, Number(p.gap) || 0)),
          padding: Math.min(48, Math.max(0, Number(p.padding) || 0)),
          radius: Math.min(32, Math.max(0, Number(p.radius) || 0)),
          color: /^#[a-f0-9]{6}$/i.test(p.color) ? p.color : "",
          background: /^#[a-f0-9]{6}$/i.test(p.background) ? p.background : "",
          columns: bounded(p.columns, 2, 1, 8),
          limit: bounded(p.limit, 8, 1, 48),
          sourceType: text(p.sourceType),
          category: text(p.category),
          keyword: text(p.keyword),
          contentIds: Array.isArray(p.contentIds)
            ? [
                ...new Set(
                  p.contentIds.map((v) => text(v, "", 160)).filter(Boolean),
                ),
              ].slice(0, 48)
            : [],
          slotId: text(p.slotId, "", 160),
          arrangement: choice(
            p.arrangement,
            ["grid", "list", "horizontal"],
            "grid",
          ),
          imagePosition: choice(
            p.imagePosition,
            ["top", "left", "right", "none"],
            "top",
          ),
          aspectRatio: choice(
            p.aspectRatio,
            ["1:1", "3:4", "9:16", "16:9", "original"],
            "3:4",
          ),
          imageFit: choice(p.imageFit, ["cover", "contain"], "cover"),
          imageSize: bounded(p.imageSize, 64, 24, 240),
          fontSize: bounded(p.fontSize, 14, 10, 32),
          showTitle: p.showTitle !== false,
          showDescription: p.showDescription === true,
          showPlays: p.showPlays === true,
          titleColor: /^#[a-f0-9]{6}$/i.test(p.titleColor) ? p.titleColor : "",
          navigationId: text(p.navigationId),
          navigationPosition: choice(
            p.navigationPosition,
            ["block", "home-top"],
            "block",
          ),
          dataConfigured: p.dataConfigured === true,
        },
      };
    });
}
export function normalizeBottomMenus(value, templates) {
  const menus = Array.isArray(value)
    ? value
    : templates.map((t) => ({
        id: t.id === "TPL-DEFAULT" ? "BM-DEFAULT" : `BM-${t.id}`,
        name: `${t.name}底部菜单`,
        status: "启用",
        items: t.pages
          .filter((p) => p.enabled && p.navigation)
          .map((p) => ({
            id: `BMI-${p.id}`,
            name: p.name,
            url: p.url,
            icon: p.id === "create" ? "plus" : "",
            status: "启用",
            sort: p.sort,
          })),
      }));
  return menus.slice(0, 100).map((m, i) => ({
    id: text(m?.id, `BM-${i + 1}`),
    name: text(m?.name, "底部菜单"),
    templateIds: templates
      .filter((t) => t.bottomMenuId === m?.id)
      .map((t) => t.id),
    status: m?.status === "停用" ? "停用" : "启用",
    items: (Array.isArray(m?.items) ? m.items : [])
      .slice(0, 8)
      .map((item, j) => ({
        id: text(item?.id, `BMI-${j + 1}`),
        name: text(item?.name, "菜单"),
        url: pageUrl(item?.url),
        icon: text(item?.icon),
        status: item?.status === "停用" ? "停用" : "启用",
        sort: Math.max(1, Number(item?.sort) || j + 1),
      }))
      .sort((a, b) => a.sort - b.sort),
  }));
}
// Template bindings remain authoritative; editing a shared bottom menu updates
// all selected templates atomically, without touching other groups' bindings.
export function setBottomMenuTemplates(config, menuId, templateIds) {
  const selected = new Set(templateIds);
  const templates = config.templates.map((t) => ({
    ...t,
    bottomMenuId: selected.has(t.id)
      ? menuId
      : t.bottomMenuId === menuId
        ? ""
        : t.bottomMenuId,
  }));
  return {
    ...config,
    templates,
    bottomMenus: normalizeBottomMenus(config.bottomMenus, templates),
  };
}
export function getBottomMenuPages(config, templateIds) {
  const templates = config.templates.filter((t) => templateIds.includes(t.id));
  if (!templates.length) return [];
  return templates[0].pages.filter(
    (p) =>
      p.enabled &&
      templates.every((t) =>
        t.pages.some((other) => other.enabled && other.url === p.url),
      ),
  );
}
export function completeTemplatePlatform(config, raw) {
  const templates = config.templates.map((t) => ({
    ...t,
    bottomMenuId: text(
      raw?.templates?.find((r) => r.id === t.id)?.bottomMenuId,
      Array.isArray(raw?.bottomMenus)
        ? ""
        : t.id === "TPL-DEFAULT"
          ? "BM-DEFAULT"
          : `BM-${t.id}`,
    ),
  }));
  const result = {
    ...config,
    revision: Math.max(0, Number(raw?.revision) || 0),
    templates,
    componentCategories: normalizeComponentCategories(raw?.componentCategories),
    componentRenderers: normalizeRenderers(raw?.componentRenderers),
    components: normalizeComponents(raw?.components),
    bottomMenus: normalizeBottomMenus(raw?.bottomMenus, templates),
  };
  return Array.isArray(raw?.components)
    ? result
    : consolidateBuiltinComponents(result).configuration;
}
export function getComponentReferences(config, id) {
  return config.templates.flatMap((t) =>
    t.pages.flatMap((p) =>
      p.blocks
        .filter((b) => b.componentId === id)
        .map((b) => `模板“${t.name}” / 页面“${p.name}” / 区块“${b.id}”`),
    ),
  );
}
export function getBottomMenuReferences(config, id) {
  return config.templates
    .filter((t) => t.bottomMenuId === id)
    .map((t) => `模板“${t.name}”`);
}
export function getPageReferences(config, templateId, pageId) {
  const t = config.templates.find((t) => t.id === templateId),
    target = t?.pages.find((p) => p.id === pageId);
  if (!target) return [];
  const refs = [],
    menu = config.bottomMenus.find((m) => m.id === t.bottomMenuId);
  for (const item of menu?.items || [])
    if (item.url === target.url)
      refs.push(`底部菜单“${menu.name}” / “${item.name}”`);
  for (const p of t.pages) {
    if (p.id === pageId) continue;
    for (const b of p.blocks) {
      const c = config.components.find((c) => c.id === b.componentId);
      if (
        c?.params.url === target.url ||
        (["video-grid", "content-list", "navigation"].includes(c?.type) &&
          target.id === "home") ||
        (c?.type === "builtin" &&
          p.enabled &&
          b.enabled &&
          (builtinDependencies[`${p.id}:${b.id}`] || []).includes(target.id))
      )
        refs.push(`页面“${p.name}” / 组件“${c.name}”`);
    }
  }
  return [...new Set(refs)];
}
const safeLink = (v) =>
  !v ||
  !!pageUrl(v) ||
  (/^https?:\/\//i.test(v) &&
    (() => {
      try {
        return ["http:", "https:"].includes(new URL(v).protocol);
      } catch {
        return false;
      }
    })());
export function validateTemplatePlatform(config, previous = null) {
  const errors = [],
    unique = (rows, label) => {
      if (new Set(rows.map((r) => r.id)).size !== rows.length)
        errors.push(`${label} ID 不能重复`);
    };
  unique(config.components, "组件");
  const rendererRows = config.componentRenderers || normalizeRenderers();
  unique(rendererRows, "渲染器");
  for (const r of rendererRows) {
    if (cssError(r.css)) errors.push(`渲染器“${r.name}”：${cssError(r.css)}`);
    for (const [key, allowed] of Object.entries(rendererFields))
      if (!allowed.includes(r.fields[key]))
        errors.push(`渲染器“${r.name}”字段 ${key} 不可用`);
    const refs = getRendererReferences(config, r.id);
    if (r.status === "停用" && refs.length)
      errors.push(`渲染器“${r.name}”被引用，不能隐藏：${refs.join("；")}`);
    const old = previous?.componentRenderers?.find((v) => v.id === r.id);
    if (old && old.kind !== r.kind && refs.length)
      errors.push(`渲染器“${r.name}”被引用，不能更换内容模块`);
  }
  for (const c of config.components) {
    const r = rendererRows.find(
      (r) => r.id === (c.rendererId || `R-${c.type}`),
    );
    if (!r || r.status === "停用")
      errors.push(`组件“${c.name}”的渲染器不存在或已隐藏`);
    else if (
      r.kind !== c.type &&
      !(
        c.type === "builtin" &&
        gridSlots.includes(c.renderer) &&
        ["content-list", "video-grid"].includes(r.kind)
      )
    )
      errors.push(`组件“${c.name}”与渲染器内容模块不匹配`);
    if (
      c.type === "navigation" &&
      !(config.navigations || []).some(
        (n) => n.id === c.params.navigationId && n.status !== "停用",
      )
    )
      errors.push(`组件“${c.name}”关联的自定义导航不存在或已隐藏`);
  }
  for (const template of config.templates) {
    for (const page of template.pages) {
      const topNavigations = page.blocks.filter(
        (b) =>
          b.enabled &&
          config.components.some(
            (c) =>
              c.id === b.componentId &&
              c.type === "navigation" &&
              c.params.navigationPosition === "home-top",
          ),
      );
      if (topNavigations.length && page.id !== "home")
        errors.push(`页面“${page.name}”不能使用首页顶部导航位置`);
      if (topNavigations.length > 1)
        errors.push(`模板“${template.name}”的首页只能引用一个顶部导航组件`);
    }
  }
  const categories = config.componentCategories || defaultComponentCategories();
  unique(categories, "组件分类");
  if (new Set(categories.map((c) => c.name)).size !== categories.length)
    errors.push("组件分类名称不能重复");
  for (const c of config.components) {
    if (!categories.some((cat) => cat.id === c.categoryId))
      errors.push(`组件“${c.name}”的分类不存在，请先解除分类关联`);
    if (c.type === "ad-slot" && !c.params.slotId)
      errors.push(`组件“${c.name}”请选择广告位`);
  }
  unique(config.bottomMenus, "底部菜单");
  for (const c of config.components) {
    if (!safeLink(c.params.url))
      errors.push(`组件“${c.name}”链接须为页面 #URL 或 HTTP/HTTPS URL`);
    if (c.params.imageUrl && !/^(https?:\/\/|\/(?!\/))/.test(c.params.imageUrl))
      errors.push(`组件“${c.name}”图片地址无效`);
    if (
      c.type === "builtin" &&
      !schema.pages.some((p) =>
        p.blocks.some((b) => `${p.id}:${b.id}` === c.renderer),
      )
    )
      errors.push(`组件“${c.name}”系统区块无效`);
    if (c.status === "停用" && getComponentReferences(config, c.id).length)
      errors.push(
        `组件“${c.name}”被引用，不能隐藏：${getComponentReferences(config, c.id).join("；")}`,
      );
  }
  for (const m of config.bottomMenus) {
    unique(m.items, `底部菜单“${m.name}”的菜单项`);
    if (!m.items.length) errors.push(`底部菜单“${m.name}”至少需要一个菜单项`);
    if (m.items.some((item) => !item.url))
      errors.push(`底部菜单“${m.name}”只能关联页面 #URL`);
    if (m.status === "停用" && getBottomMenuReferences(config, m.id).length)
      errors.push(`底部菜单“${m.name}”被模板引用，不能隐藏`);
  }
  for (const t of config.templates) {
    unique(t.pages, `模板“${t.name}”页面`);
    if (new Set(t.pages.map((p) => p.url)).size !== t.pages.length)
      errors.push(`模板“${t.name}”页面 URL 不能重复`);
    for (const page of t.pages) {
      if (
        t.pages.some(
          (other) => other.id !== page.id && page.url === `#${other.id}`,
        )
      )
        errors.push(
          `模板“${t.name}”页面 URL ${page.url} 与其他页面标识冲突，请使用其他名称`,
        );
    }
    const available = (url) => t.pages.some((p) => p.url === url && p.enabled),
      menu = config.bottomMenus.find((m) => m.id === t.bottomMenuId);
    if (t.bottomMenuId && (!menu || menu.status === "停用"))
      errors.push(`模板“${t.name}”引用的底部菜单不存在或已隐藏`);
    for (const item of menu?.items || [])
      if (!available(item.url))
        errors.push(
          `模板“${t.name}” / 底部菜单“${item.name}”指向不存在或隐藏的页面 ${item.url}`,
        );
    for (const p of t.pages) {
      unique(p.blocks, `页面“${p.name}”区块`);
      for (const b of p.blocks) {
        const c = config.components.find((c) => c.id === b.componentId);
        if (!c || c.status === "停用") {
          errors.push(
            `模板“${t.name}” / 页面“${p.name}”引用的组件 ${b.componentId} 不存在或已隐藏`,
          );
          continue;
        }
        if (c.type === "builtin" && !builtinComponentMatches(c, p.id, b.id))
          errors.push(`页面“${p.name}”区块“${b.id}”不能调用其他系统区块`);
        const locked = schema.pages
          .find((base) => base.id === p.id)
          ?.blocks.find((base) => base.id === b.id)?.locked;
        if (locked && c.type !== "builtin")
          errors.push(`页面“${p.name}”必要区块“${b.id}”必须使用对应系统组件`);
        if (
          c.params.url.startsWith("#") &&
          !available(c.params.url) &&
          !(c.params.url === p.url && !p.enabled)
        )
          errors.push(
            `模板“${t.name}” / 页面“${p.name}” / 组件“${c.name}”链接 ${c.params.url} 不存在或已隐藏`,
          );
        const targets = ["video-grid", "content-list", "navigation"].includes(
          c.type,
        )
          ? ["home"]
          : c.type === "builtin" && p.enabled && b.enabled
            ? builtinDependencies[`${p.id}:${b.id}`] || []
            : [];
        for (const target of targets)
          if (!t.pages.some((p) => p.id === target && p.enabled))
            errors.push(
              `页面“${p.name}” / 组件“${c.name}”关联页面 #${target} 不存在或已隐藏`,
            );
      }
      if (!p.enabled) {
        const refs = getPageReferences(config, t.id, p.id);
        if (refs.length)
          errors.push(`页面“${p.name}”被引用，不能隐藏：${refs.join("；")}`);
      }
    }
  }
  if (previous) {
    for (const c of previous.components)
      if (
        !config.components.some((n) => n.id === c.id) &&
        getComponentReferences(config, c.id).length
      )
        errors.push(`组件“${c.name}”被页面引用，不能删除`);
    for (const m of previous.bottomMenus)
      if (
        !config.bottomMenus.some((n) => n.id === m.id) &&
        getBottomMenuReferences(config, m.id).length
      )
        errors.push(`底部菜单“${m.name}”被模板引用，不能删除`);
  }
  return [...new Set(errors)];
}
